import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { Thumbnail } from '../../partials/image/image.types';
import { AdditionalStyleSheet } from '../../components.utils';
import { ImageWithFigcaption } from '../../partials/image/imageWithFigcaption/ImageWithFigcaption';
import { Image } from '../../partials/image/Image';
import { Jw } from '../media/Jw';
import { Iframe } from '../media/Iframe';
import { MMPlayer } from '../media/MMPlayer';
import { createImageWidths, getImageCaption } from '../../partials/image/image.utils';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

interface ArticleMainImageThemeProps {
  baseUnit: BaseUnit;
}

export interface JwCoverBlock {
  videoId: string;
  html: string;
  type: string;
}

export interface HTMLCoverBlock {
  html: string;
  type: string;
}

export type Media = JwCoverBlock | HTMLCoverBlock;

interface ArticleMainImageDataProps {
  image: Thumbnail;
  media: Media | null;
  siteName: string;
  templateName: string;
}

const getMainImageStyle = (baseUnit: BaseUnit) => {
  return StyleSheet.create({
    style: {
      marginTop: `${baseUnit}px`,
      marginBottom: `${baseUnit * 0.25}px`,
    },
  });
};

const getCoverStyles = () => {
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
    },
  });
};

const getBlockStyle = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      margin: 0,
    },
  });
};

const imageWidths = createImageWidths(540, 720, 720);

const getMainImage = (image: Thumbnail, credit: string | undefined, caption: string | undefined, style: AdditionalStyleSheet) => {
  const figCaption = getImageCaption(credit, caption);
  return figCaption
    ? <ImageWithFigcaption {...image} figcaption={figCaption} additionalStyle={style} imageWidths={imageWidths} />
    : <Image {...image} additionalStyle={style} imageWidths={imageWidths} />;
};

const getMedia = (media: Media, siteName: string, templateName: string) => {
  const additionalStyle = getBlockStyle() as AdditionalStyleSheet;
  const { type } = media;
  switch (type) {
    case 'jw': {
      const { html, videoId } = media as JwCoverBlock;
      return <Jw html={html} additionalStyle={additionalStyle} videoId={videoId} siteName={siteName} templateName={templateName} />;
    }

    case 'youtube': {
      const { html } = media as HTMLCoverBlock;
      return <Iframe html={html} additionalStyle={additionalStyle} />;
    }

    case 'mmPlayer': {
      const { html } = media as HTMLCoverBlock;
      return <MMPlayer html={html} additionalStyle={additionalStyle} />;
    }
    default: return null;
  }
};

export const ArticleMainImage: React.FunctionComponent<ArticleMainImageDataProps> = ({ image, media, siteName, templateName }) => {
  const { baseUnit } = useTheme<ArticleMainImageThemeProps>();
  const mainImageStyle = getMainImageStyle(baseUnit) as AdditionalStyleSheet;
  const coverStyles = getCoverStyles();
  const { credit, caption } = image;
  return (
    <ErrorBoundary>
      <div className={css(coverStyles.wrapper)}>
        {getMainImage(image, credit, caption, mainImageStyle)}
        {media && getMedia(media, siteName, templateName)}
      </div>
    </ErrorBoundary>
  );
};
